import React from 'react'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'

import ClearableInput, {
  SettingsField,
} from '../../../components/Settings/ClearableInput'
import SettingsCard from '../../../components/Settings/SettingsCard'
import { socialUrlStatus } from '../../../lib/socialLinks'

export default function CharityDonationLinksCard({
  charity,
  onFieldChange,
  onSave,
  hasChanged,
  isSaving,
}) {
  const { t } = useTranslation()

  const donationStatus = socialUrlStatus(charity?.external_donation_url)
  const amazonStatus = socialUrlStatus(charity?.amazon_wishlist_url)
  const anyInvalid = donationStatus === 'invalid' || amazonStatus === 'invalid'

  return (
    <SettingsCard
      icon="hand-holding-heart"
      title={t('charities.settings.donation_links_title')}
      actions={
        <>
          <button
            className="btn btn-primary"
            type="button"
            onClick={onSave}
            disabled={!hasChanged || isSaving || anyInvalid}
          >
            {isSaving ? t('users.edit.saving') : t('charities.settings.save')}
          </button>
          {anyInvalid && (
            <span className="text-danger small">
              {t(
                'users.edit.socials_invalid',
                'Fix the highlighted links before saving.',
              )}
            </span>
          )}
        </>
      }
    >
      <div className="row g-3">
        <div className="col-md-6">
          <SettingsField
            label={t('charities.settings.external_donation_url')}
            htmlFor="external_donation_url"
            status={donationStatus}
            help={
              donationStatus === 'invalid'
                ? t(
                    'users.edit.social_invalid_url',
                    'Enter a full URL, e.g. https://example.com/you',
                  )
                : null
            }
            helpVariant={donationStatus === 'invalid' ? 'error' : undefined}
          >
            <ClearableInput
              id="external_donation_url"
              name="external_donation_url"
              type="url"
              placeholder="https://donate.example.org"
              value={charity?.external_donation_url || ''}
              onChange={value => onFieldChange('external_donation_url', value)}
              onClear={() => onFieldChange('external_donation_url', '')}
              clearLabel={t('settings.field.clear', 'Clear')}
            />
          </SettingsField>
        </div>
        <div className="col-md-6">
          <SettingsField
            label={t('charities.settings.amazon_wishlist_url')}
            htmlFor="amazon_wishlist_url"
            status={amazonStatus}
            help={
              amazonStatus === 'invalid'
                ? t(
                    'users.edit.social_invalid_url',
                    'Enter a full URL, e.g. https://example.com/you',
                  )
                : null
            }
            helpVariant={amazonStatus === 'invalid' ? 'error' : undefined}
          >
            <ClearableInput
              id="amazon_wishlist_url"
              name="amazon_wishlist_url"
              type="url"
              placeholder="https://www.amazon.com/hz/wishlist/ls/..."
              value={charity?.amazon_wishlist_url || ''}
              onChange={value => onFieldChange('amazon_wishlist_url', value)}
              onClear={() => onFieldChange('amazon_wishlist_url', '')}
              clearLabel={t('settings.field.clear', 'Clear')}
            />
          </SettingsField>
        </div>
      </div>
    </SettingsCard>
  )
}

CharityDonationLinksCard.propTypes = {
  charity: PropTypes.object,
  onFieldChange: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  hasChanged: PropTypes.bool,
  isSaving: PropTypes.bool,
}
